import { IMessenger } from "interfaces/messenger";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { formatDate } from "helpers/formatDate";
import { phoneApplyMask } from "helpers/utils";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { whatsappMessage } from "helpers/whatsapp";
import MDButton from "components/MDButton";

interface ModalMessengerProps {
  messenger: IMessenger;
  deleteMessenger: (idMsg: number) => void;
  onClose?: () => void;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ModalMessenger: React.FC<ModalMessengerProps> = ({
  messenger,
  deleteMessenger,
  onClose,
}) => {
  const theme = useTheme();

  const handleWhatsappMessage = () => {
    whatsappMessage({
      message: `Olá, *${messenger.nome.toUpperCase()}*, somos da TNV Team Sports.`,
      phone: messenger.phoneCel,
    });
  };

  const contactListItemSx = { py: 0.35, px: 1.5, alignItems: "flex-start" };
  const contactIconSx = { minWidth: 32, mt: 0.15 };
  const contactTextSlots = {
    primaryTypographyProps: {
      sx: { mb: 0, lineHeight: 1.15 },
    },
    secondaryTypographyProps: {
      component: "div" as const,
      sx: { mt: 0.15, lineHeight: 1.35 },
    },
  };

  return (
    <MDBox
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.12
          )} 0%, ${alpha(theme.palette.primary.dark, 0.06)} 100%)`,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              fontSize: "1.25rem",
              fontWeight: 700,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {initialsFromName(messenger.nome)}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={1}
            >
              <Box minWidth={0}>
                <MDTypography variant="h5" fontWeight="bold" noWrap>
                  {messenger.nome}
                </MDTypography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  Contato pelo site
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Excluir mensagem"
                  onClick={() => deleteMessenger(messenger.id)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
              <Chip
                size="small"
                variant="filled"
                icon={<MarkEmailReadOutlinedIcon />}
                label={messenger.status ? "Lida" : "Não lida"}
                color={messenger.status ? "success" : "warning"}
                sx={{ fontWeight: 600, p: 1.5 }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Corpo rolável */}
      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <List dense disablePadding sx={{ py: 2 }}>
            <ListItem sx={contactListItemSx}>
              <ListItemIcon sx={contactIconSx}>
                <EmailOutlinedIcon color="action" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                {...contactTextSlots}
                primary={
                  <MDTypography variant="caption" color="text.secondary">
                    E-mail
                  </MDTypography>
                }
                secondary={
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${messenger.email}`}
                    sx={{ color: "primary.main", wordBreak: "break-all" }}
                  >
                    {messenger.email}
                  </Typography>
                }
              />
            </ListItem>
            <Divider component="li" variant="inset" sx={{ my: 0 }} />
            <ListItem sx={contactListItemSx}>
              <ListItemIcon sx={contactIconSx}>
                <PhoneIphoneOutlinedIcon color="action" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                {...contactTextSlots}
                primary={
                  <Typography variant="caption" color="text.secondary">
                    Celular
                  </Typography>
                }
                secondary={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">
                      {phoneApplyMask(messenger.phoneCel)}
                    </Typography>
                    <IconButton
                      size="small"
                      color="success"
                      aria-label="Abrir WhatsApp"
                      onClick={handleWhatsappMessage}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
              />
            </ListItem>
            <Divider component="li" variant="inset" sx={{ my: 0 }} />
            <ListItem sx={contactListItemSx}>
              <ListItemIcon sx={contactIconSx}>
                <BusinessOutlinedIcon color="action" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                {...contactTextSlots}
                primary={
                  <Typography variant="caption" color="text.secondary">
                    Empresa
                  </Typography>
                }
                secondary={
                  <Typography variant="body2">
                    {messenger.empresa || "—"}
                  </Typography>
                }
              />
            </ListItem>
            <Divider component="li" variant="inset" sx={{ my: 0 }} />
            <ListItem sx={contactListItemSx}>
              <ListItemIcon sx={contactIconSx}>
                <GroupsOutlinedIcon color="action" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                {...contactTextSlots}
                primary={
                  <Typography variant="caption" color="text.secondary">
                    Equipe
                  </Typography>
                }
                secondary={
                  <Typography variant="body2">
                    {messenger.equipe || "—"}
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Paper>

        <Typography
          variant="overline"
          color="text.secondary"
          letterSpacing={1.2}
          display="block"
          mb={1}
        >
          Mensagem
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: (t) => alpha(t.palette.grey[500], 0.08),
            border: 1,
            borderColor: "divider",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 12,
              top: -8,
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderBottom: (t) =>
                `8px solid ${alpha(t.palette.grey[500], 0.08)}`,
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.7,
            }}
          >
            {messenger.message?.trim() ? messenger.message : null}
          </Typography>
          {!messenger.message?.trim() && (
            <Typography
              color="text.secondary"
              fontStyle="italic"
              variant="body2"
            >
              (Sem texto)
            </Typography>
          )}
        </Paper>

        <Stack spacing={0.5} mt={2}>
          <Typography variant="caption" color="text.secondary">
            Enviada em{" "}
            <strong>
              {formatDate(messenger.createAt, "dd/MM/yyyy HH:mm")}
            </strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Última atualização{" "}
            <strong>
              {formatDate(messenger.updatedAt, "dd/MM/yyyy HH:mm")}
            </strong>
          </Typography>
        </Stack>
      </Box>

      {/* Rodapé ações */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <MDButton variant="outlined" color="dark" onClick={onClose}>
            Fechar
          </MDButton>
          <MDButton
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsappMessage}
          >
            Responder no WhatsApp
          </MDButton>
        </Stack>
      </Box>
    </MDBox>
  );
};

export default ModalMessenger;
